import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const pillars = [
  {
    num: '01',
    title: 'Gobierno',
    description: 'Diseñamos e implementamos órganos de decisión que aportan orden, claridad y transparencia a la empresa familiar.',
  },
  {
    num: '02',
    title: 'Continuidad',
    description: 'Preparamos procesos de sucesión que preservan la armonía y protegen el patrimonio, asegurando que el liderazgo transite de una generación a la siguiente.',
  },
  {
    num: '03',
    title: 'Confianza',
    description: 'Actuamos con la discreción y confidencialidad que cada momento requiere, construyendo relaciones duraderas basadas en la confianza.',
  },
];

export default function ValuesSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      // Label animation
      gsap.from('.values-label', {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: 'expo.out',
        scrollTrigger: { trigger: section, start: 'top 80%' },
      });

      // Heading animation
      gsap.from('.values-heading', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'expo.out',
        delay: 0.1,
        scrollTrigger: { trigger: section, start: 'top 80%' },
      });

      // Gold line
      gsap.from('.values-line', {
        scaleX: 0,
        duration: 1,
        ease: 'expo.out',
        delay: 0.2,
        scrollTrigger: { trigger: section, start: 'top 80%' },
      });

      // Pillar cards staggered
      gsap.from('.pillar-card', {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.15,
        ease: 'expo.out',
        scrollTrigger: { trigger: '.pillars-grid', start: 'top 85%' },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="valores"
      className="relative w-full bg-[#0f1419] py-32 md:py-40"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center mb-20 md:mb-28">
          <span className="values-label inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-[#C9A26E]/70 mb-6">
            Propuesta de Valor
          </span>
          <h2 className="values-heading font-heading text-[#f0ebe2] text-[clamp(28px,4vw,52px)] leading-[1.1] tracking-[-0.01em] mb-6">
            Tres pilares, una firma
          </h2>
          <div className="values-line w-12 h-px bg-[#C9A26E] mx-auto origin-center" />
        </div>

        {/* Pillars Grid */}
        <div className="pillars-grid grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C9A26E]/10">
          {pillars.map((pillar, i) => (
            <div
              key={i}
              className="pillar-card group relative bg-[#0f1419] p-10 md:p-12 hover:bg-[#141c24] transition-colors duration-500"
            >
              {/* Number */}
              <span className="block font-mono text-[11px] tracking-[0.15em] text-[#C9A26E]/50 mb-8">
                {pillar.num}
              </span>

              {/* Title */}
              <h3 className="font-heading text-[#f0ebe2] text-2xl md:text-3xl mb-6 group-hover:text-[#C9A26E] transition-colors duration-400">
                {pillar.title}
              </h3>

              {/* Decorative line */}
              <div className="w-8 h-px bg-[#C9A26E]/30 mb-6 group-hover:w-12 group-hover:bg-[#C9A26E]/60 transition-all duration-400" />

              {/* Description */}
              <p className="font-body text-[#8a8075] text-sm leading-[1.8]">
                {pillar.description}
              </p>

              {/* Corner accent on hover */}
              <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-[#C9A26E]/0 group-hover:border-[#C9A26E]/30 transition-all duration-500" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-[#C9A26E]/0 group-hover:border-[#C9A26E]/30 transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
